import { useState, useEffect } from 'react'
import {
  Box, Card, CardContent, Typography, Switch, Button, IconButton,
  TextField, Dialog, DialogTitle, DialogContent, DialogActions,
  List, ListItem, ListItemText, ListItemSecondaryAction, Chip,
} from '@mui/material'
import { Add, DragIndicator, Delete } from '@mui/icons-material'

// Fallback demo data used when API returns empty
const DEFAULT_PRESETS = [
  { id: 1, name: 'Customer request — expedite', enabled: true },
  { id: 2, name: 'Safety stock replenishment', enabled: true },
  { id: 3, name: 'Raw material availability', enabled: true },
  { id: 4, name: 'Line capacity constraint', enabled: true },
  { id: 5, name: 'Seasonal demand shift', enabled: true },
  { id: 6, name: 'Quality hold — rework', enabled: true },
  { id: 7, name: 'Co-pack / private label priority', enabled: true },
  { id: 8, name: 'Promotional build', enabled: true },
  { id: 9, name: 'End-of-life run-out', enabled: false },
]

export default function RationalePresets({ settings, onSave }) {
  const [presets, setPresets] = useState(() =>
    Array.isArray(settings?.rationalePresets) && settings.rationalePresets.length > 0
      ? settings.rationalePresets
      : DEFAULT_PRESETS,
  )
  const [addOpen, setAddOpen] = useState(false)
  const [newName, setNewName] = useState('')
  const [dragIdx, setDragIdx] = useState(null)

  useEffect(() => {
    if (Array.isArray(settings?.rationalePresets) && settings.rationalePresets.length > 0) {
      setPresets(settings.rationalePresets)
    }
  }, [settings])

  const toggle = (id) => {
    setPresets((prev) =>
      prev.map((p) => (p.id === id ? { ...p, enabled: !p.enabled } : p)),
    )
  }

  const remove = (id) => {
    setPresets((prev) => prev.filter((p) => p.id !== id))
  }

  const addPreset = () => {
    if (!newName.trim()) return
    const updated = [
      ...presets,
      { id: Date.now(), name: newName.trim(), enabled: true },
    ]
    setPresets(updated)
    setNewName('')
    setAddOpen(false)
    if (onSave) onSave({ ...settings, rationalePresets: updated }).catch(() => {})
  }

  const handleDragStart = (idx) => setDragIdx(idx)

  const handleDragOver = (e, idx) => {
    e.preventDefault()
    if (dragIdx === null || dragIdx === idx) return
    setPresets((prev) => {
      const next = [...prev]
      const [moved] = next.splice(dragIdx, 1)
      next.splice(idx, 0, moved)
      return next
    })
    setDragIdx(idx)
  }

  const handleDragEnd = () => setDragIdx(null)

  const enabledCount = presets.filter((p) => p.enabled).length

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Box>
          <Typography variant="body2" color="text.secondary">
            Override rationale presets appear when planners adjust scheduled quantities.
            Drag to reorder. Disable presets to hide them from the selection list.
          </Typography>
        </Box>
        <Chip label={`${enabledCount} active`} size="small" color="primary" variant="outlined" />
      </Box>

      <Card variant="outlined">
        <List disablePadding>
          {presets.map((preset, idx) => (
            <ListItem
              key={preset.id}
              divider={idx < presets.length - 1}
              draggable
              onDragStart={() => handleDragStart(idx)}
              onDragOver={(e) => handleDragOver(e, idx)}
              onDragEnd={handleDragEnd}
              sx={{
                cursor: 'grab',
                opacity: preset.enabled ? 1 : 0.5,
                bgcolor: dragIdx === idx ? 'action.hover' : 'transparent',
                '&:hover': { bgcolor: 'action.hover' },
              }}
            >
              <DragIndicator sx={{ color: 'text.disabled', mr: 1.5 }} />
              <ListItemText
                primary={preset.name}
                secondary={preset.enabled ? 'Active' : 'Disabled'}
              />
              <ListItemSecondaryAction>
                <Switch
                  checked={preset.enabled}
                  onChange={() => toggle(preset.id)}
                  size="small"
                />
                <IconButton size="small" onClick={() => remove(preset.id)} sx={{ ml: 1 }}>
                  <Delete fontSize="small" />
                </IconButton>
              </ListItemSecondaryAction>
            </ListItem>
          ))}
        </List>
      </Card>

      <Button startIcon={<Add />} sx={{ mt: 2 }} onClick={() => setAddOpen(true)}>
        Add New Preset
      </Button>

      <Dialog open={addOpen} onClose={() => setAddOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Add Rationale Preset</DialogTitle>
        <DialogContent>
          <TextField
            label="Preset Name"
            fullWidth
            autoFocus
            value={newName}
            onChange={(e) => setNewName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && addPreset()}
            sx={{ mt: 1 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAddOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={addPreset} disabled={!newName.trim()}>
            Add
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}
